import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Layer, Source, useMap } from 'react-map-gl';
import type { MapLayerMouseEvent } from 'react-map-gl';
import type { Feature, Position } from 'geojson';
import { CLOSE_TOLERANCE_PX } from './utils';

const SOURCE = 'area-edit-draft';

interface DrawPolygonGestureProps {
  color: string;
  /** A finished ring, open (first vertex not repeated). */
  onCommit: (ring: Position[]) => void;
  /** Pulls each vertex onto a nearby boundary as it is placed, while Snap is on. */
  snapPosition?: (position: Position) => Position;
}

/**
 * Draw polygon, as the mock has it: each click adds a vertex; clicking the first
 * vertex or double-clicking finishes; Backspace removes the last vertex; Esc
 * cancels. The shape in progress fills at 0.2 under a dashed 2px line.
 *
 * @param {DrawPolygonGestureProps} props - The colour and the commit.
 * @return {JSX.Element} The draft layers.
 */
export function DrawPolygonGesture({
  color,
  onCommit,
  snapPosition,
}: DrawPolygonGestureProps): JSX.Element {
  const { current: mapRef } = useMap();
  const [vertices, setVertices] = useState<Position[]>([]);
  const [pointer, setPointer] = useState<Position | null>(null);
  const latest = useRef({ vertices, onCommit, snapPosition });
  useLayoutEffect(() => {
    latest.current = { vertices, onCommit, snapPosition };
  }, [vertices, onCommit, snapPosition]);

  useEffect(() => {
    if (!mapRef) return undefined;
    const map = mapRef.getMap();
    const canvas = map.getCanvas();
    canvas.style.cursor = 'crosshair';
    map.doubleClickZoom.disable();

    /** Closes the ring if it has three corners, and starts afresh either way. */
    const finish = () => {
      // Drop repeats a double-click leaves behind.
      const ring = latest.current.vertices.filter(
        (position, i, all) =>
          i === 0 ||
          map
            .project(position as [number, number])
            .dist(map.project(all[i - 1] as [number, number])) >= 4
      );
      setVertices([]);
      setPointer(null);
      if (ring.length >= 3) latest.current.onCommit(ring);
    };

    /**
     * Adds a vertex, or closes the ring on its first one.
     *
     * @param {MapLayerMouseEvent} event - The click.
     */
    const handleClick = (event: MapLayerMouseEvent) => {
      const current = latest.current.vertices;
      const raw: Position = [event.lngLat.lng, event.lngLat.lat];
      const position = latest.current.snapPosition?.(raw) ?? raw;
      // The first vertex may have been snapped off the spot first clicked, so a
      // click back near either that spot or the snapped vertex closes the ring.
      const first =
        current.length >= 3
          ? map.project(current[0] as [number, number])
          : null;
      if (
        first &&
        (first.dist(event.point) < CLOSE_TOLERANCE_PX ||
          first.dist(map.project(position as [number, number])) <
            CLOSE_TOLERANCE_PX)
      ) {
        finish();
        return;
      }
      setVertices([...current, position]);
    };

    /**
     * Follows the pointer, so the next edge shows before it is placed.
     *
     * @param {MapLayerMouseEvent} event - The move.
     */
    const handleMouseMove = (event: MapLayerMouseEvent) => {
      if (latest.current.vertices.length > 0) {
        setPointer([event.lngLat.lng, event.lngLat.lat]);
      }
    };

    /**
     * Finishes on a double-click.
     *
     * @param {MapLayerMouseEvent} event - The double-click.
     */
    const handleDoubleClick = (event: MapLayerMouseEvent) => {
      event.preventDefault();
      finish();
    };

    /**
     * Backspace takes back the last vertex; Esc drops the shape.
     *
     * @param {KeyboardEvent} event - The key press.
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      // A key typed into a field, such as the reference search, is the field's.
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('input, textarea, [contenteditable="true"]')
      ) {
        return;
      }
      if (event.key === 'Escape') {
        if (latest.current.vertices.length === 0) return;
        event.stopPropagation();
        setVertices([]);
        setPointer(null);
      } else if (
        event.key === 'Backspace' &&
        latest.current.vertices.length > 0
      ) {
        setVertices(latest.current.vertices.slice(0, -1));
      }
    };

    map.on('click', handleClick);
    map.on('mousemove', handleMouseMove);
    map.on('dblclick', handleDoubleClick);
    // Heard in the capture phase and stopped while there is something to cancel,
    // so Esc drops the shape rather than also closing the modal or panel around
    // the map. With nothing in progress it passes on and closes them as usual.
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      map.off('click', handleClick);
      map.off('mousemove', handleMouseMove);
      map.off('dblclick', handleDoubleClick);
      window.removeEventListener('keydown', handleKeyDown, true);
      map.doubleClickZoom.enable();
      canvas.style.cursor = '';
    };
  }, [mapRef]);

  const points = pointer ? [...vertices, pointer] : vertices;
  const features: Feature[] = [];
  if (points.length >= 3) {
    features.push({
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: [[...points, points[0]]] },
    });
  } else if (points.length === 2) {
    features.push({
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: points },
    });
  }
  points.forEach((position) =>
    features.push({
      type: 'Feature',
      properties: {},
      geometry: { type: 'Point', coordinates: position },
    })
  );

  return (
    <Source
      id={SOURCE}
      type="geojson"
      data={{ type: 'FeatureCollection', features }}
    >
      <Layer
        id={`${SOURCE}-fill`}
        type="fill"
        filter={['==', ['geometry-type'], 'Polygon']}
        paint={{ 'fill-color': color, 'fill-opacity': 0.2 }}
      />
      <Layer
        id={`${SOURCE}-line`}
        type="line"
        filter={['!=', ['geometry-type'], 'Point']}
        paint={{
          'line-color': color,
          'line-width': 2,
          'line-dasharray': [2, 1],
        }}
      />
      <Layer
        id={`${SOURCE}-points`}
        type="circle"
        filter={['==', ['geometry-type'], 'Point']}
        paint={{
          'circle-radius': 5,
          'circle-color': color,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2,
        }}
      />
    </Source>
  );
}
